import React, { useState, useEffect, useRef } from "react";
import { useCollection } from "@cloudscape-design/collection-hooks";
import { withBundle, WithBundleProps } from "@amzn/react-arb-tools";
import CloudScapeTable from "@cloudscape-design/components/table";
import CloudScapePagination from "@cloudscape-design/components/pagination";
import CloudScapeTextFilter from "@cloudscape-design/components/text-filter";
import { getTasksPageTableColumnDefinition } from "./TasksPageTableColumnDefinition";
import {
  TableNoMatchState,
  TableEmptyState,
} from "../../components/Tables/TableComponents";
import { FullPageHeader } from "../../components/Header/FullPageHeader";
import {
  getTextFilterCounterText,
  getHeaderCounterText,
} from "../../utils/HeaderUtils";
import { getTasksAPI } from "../../api/getTasksAPI";
import { PAGE_TITLE, PAGE_SIZE } from "../../constants/constants";

export const TasksPageTable = withBundle("pages.TasksPageTable")(
  (props: WithBundleProps) => {
    const [isLoadingTasks, setLoadingTasks] = useState<boolean>(true);
    const [tasks, setTasks] = useState<any>([]);
    const [selectedTasks, setSelectedTasks] = useState<any[]>([]);
    const columnDefinitions = useRef(
      getTasksPageTableColumnDefinition(props.bundle)
    );

    useEffect(() => {
      setTasks(getTasksAPI());
      setLoadingTasks(false);
    }, []);

    const {
      items,
      actions,
      filteredItemsCount,
      collectionProps,
      filterProps,
      paginationProps,
    } = useCollection(
      tasks.map((task: any) => {
        return {
          ...task,
          severity: props.bundle.formatMessage("severity_lable", {
            severity: task.severity,
          }),
          active: task.active
            ? props.bundle.getMessage("active")
            : props.bundle.getMessage("inactive"),
          lastUpdated: props.bundle.formatMessage("last_updated_date", {
            lastUpdated: new Date(0).setUTCSeconds(task.lastUpdated),
          }),
        };
      }),
      {
        pagination: { pageSize: PAGE_SIZE },
        sorting: {},
        filtering: {
          empty: <TableEmptyState contentName={PAGE_TITLE.TASKS} />,
          noMatch: (
            <TableNoMatchState
              onClearFilter={() => actions.setFiltering("")}
              page={PAGE_TITLE.TASKS}
            />
          ),
        },
        selection: {},
      }
    );

    return (
      <React.Fragment>
        <CloudScapeTable
          {...collectionProps}
          //Tasks Table Properties
          variant="full-page" //variant table is entire content of page
          stickyHeader={true} //table header remains visible when the user scrolls down
          resizableColumns={true}
          selectionType="multi"
          stickyColumns={{ first: 1, last: 0 }}
          sortingDisabled={isLoadingTasks} //disabling user from sorting before loading the table content
          //Table Content
          items={items}
          columnDefinitions={columnDefinitions.current}
          //empty slot when table items array is empty
          empty={<TableEmptyState contentName={PAGE_TITLE.TASKS} />}
          //slot to add Heading element of table container
          header={
            <FullPageHeader
              currentRecords={selectedTasks}
              selectedRecords={selectedTasks.length}
              totalRecords={getHeaderCounterText(
                tasks.length,
                selectedTasks.length
              )}
              page={PAGE_TITLE.TASKS}
            />
          }
          //Loading condition and UI
          loading={isLoadingTasks}
          loadingText={props.bundle.getMessage(`loading_${PAGE_TITLE.TASKS}`)}
          //slot to add filter controls for table
          filter={
            <CloudScapeTextFilter
              {...filterProps}
              filteringAriaLabel={props.bundle.getMessage(
                `filter_${PAGE_TITLE.TASKS}`
              )}
              filteringPlaceholder={props.bundle.getMessage(
                `search_${PAGE_TITLE.TASKS}`
              )}
              filteringClearAriaLabel={props.bundle.getMessage("clear")}
              countText={getTextFilterCounterText(
                filteredItemsCount || 0,
                props.bundle
              )}
            />
          }
          //slot to add pagination component of table
          pagination={<CloudScapePagination {...paginationProps} />}
          //Table Selection
          onSelectionChange={({ detail }: any) =>
            setSelectedTasks(detail.selectedItems)
          }
          selectedItems={selectedTasks}
          trackBy="taskName" //specifies property that uniquely identifies individual item & acts as key for react rendering performance optimization
        />
      </React.Fragment>
    );
  }
);
